/**
 * Registry of the tenant storefront's public pages (KAN-1542).
 *
 * The one place that says which public pages exist, which of them a
 * tenant may hide, where they are linked from and whether the sitemap
 * lists them. Header, footer, the route gate, the visibility editor and
 * the backend all read it; before this, five hand-kept copies had to
 * agree and nothing checked that they did.
 *
 * The backend reads the generated copy in
 * ``apps/api/app/data/public_pages.json`` (sitemap, the saved
 * ``pages_visibility`` map). ``tests/publicPageRegistry.test.ts`` fails when the
 * two drift and rewrites the file with ``-u`` — the same arrangement as
 * the admin page catalog (KAN-1373).
 *
 * Order is the order of the visibility editor and of every link list.
 *
 * A page may also list its **blocks** (KAN-1635) — the sections it is
 * built from, in the order it renders them. The order is fixed; a tenant
 * switches a hideable block off, and picks a layout variant where
 * ``SECTION_VARIANTS`` (shell/siteThemes.ts) has one under the block's key.
 */

/** One section of a public page (KAN-1635). */
export interface PageBlock {
  key: string;
  /** Name of the block in the block editor. */
  labelKey: string;
  /** Whether a tenant may switch the block off. */
  hideable: boolean;
  /** Why a required block cannot be hidden — shown next to its lock. */
  lockReasonKey?: string;
}

export interface PublicPage {
  key: string;
  /** Route path under ``/:lang`` — ``""`` is the landing. */
  path: string;
  /** Whether a tenant may switch the page off. */
  hideable: boolean;
  /** Whether ``/sitemap.xml`` lists it (param routes cannot be listed). */
  inSitemap: boolean;
  /** Name of the page in the visibility editor. */
  labelKey: string;
  /** Why a required page cannot be hidden — shown next to its lock. */
  lockReasonKey?: string;
  /** Hideable page the „Minimální web" preset keeps switched on. */
  keepInMinimalWeb?: true;
  /**
   * The switch hides only the page's menu entry; the route stays
   * reachable because other flows link into it (KAN-1563).
   */
  menuOnly?: true;
  /** One sentence under the switch in the visibility editors. */
  hintKey?: string;
  /** Primary nav entry in the header, if any. */
  header?: {
    /** i18n key under ``common:nav``. */
    i18nKey: "howItWorks" | "materials" | "pricing" | "contact";
    testId: string;
  };
  /** Footer link, if any. */
  footer?: {
    column: "product" | "customer";
    /** i18n key in ``common``. */
    labelKey: string;
  };
  /** The page's blocks in render order (KAN-1635). */
  blocks?: readonly PageBlock[];
}

export const PUBLIC_PAGES = [
  {
    key: "home",
    path: "",
    hideable: false,
    inSitemap: true,
    labelKey: "admin:publicPages.pages.home",
    lockReasonKey: "admin:publicPages.lockReason.quote",
    footer: { column: "customer", labelKey: "nav.startQuote" },
    // ``/services/:slug`` renders the same landing scoped to one process,
    // so these blocks apply there too.
    blocks: [
      {
        key: "hero",
        labelKey: "admin:pageBlocks.blocks.hero",
        hideable: false,
        lockReasonKey: "admin:publicPages.lockReason.quote",
      },
      { key: "process_picker", labelKey: "admin:pageBlocks.blocks.processPicker", hideable: true },
      { key: "trust", labelKey: "admin:pageBlocks.blocks.trust", hideable: true },
      { key: "steps", labelKey: "admin:pageBlocks.blocks.steps", hideable: true },
      { key: "benefits", labelKey: "admin:pageBlocks.blocks.benefits", hideable: true },
      { key: "faq", labelKey: "admin:pageBlocks.blocks.faq", hideable: true },
      { key: "contact_form", labelKey: "admin:pageBlocks.blocks.contactForm", hideable: true },
      { key: "final_cta", labelKey: "admin:pageBlocks.blocks.finalCta", hideable: true },
    ],
  },
  {
    key: "how_it_works",
    path: "how-it-works",
    hideable: true,
    inSitemap: true,
    labelKey: "common:nav.howItWorks",
    header: { i18nKey: "howItWorks", testId: "nav-how-it-works" },
    footer: { column: "product", labelKey: "footer.howItWorks" },
    // Sub-pages list only the optional blocks around their data core
    // (owner, 2026-09-25): the core — steps, catalog, rates, forms — is
    // the page itself and is not listed.
    blocks: [
      { key: "delivery_modes", labelKey: "admin:pageBlocks.blocks.deliveryModes", hideable: true },
      { key: "faq", labelKey: "admin:pageBlocks.blocks.faq", hideable: true },
      { key: "final_cta", labelKey: "admin:pageBlocks.blocks.finalCta", hideable: true },
    ],
  },
  {
    key: "materials",
    path: "materials",
    hideable: true,
    inSitemap: true,
    labelKey: "common:nav.materials",
    header: { i18nKey: "materials", testId: "nav-materials" },
    footer: { column: "product", labelKey: "nav.materials" },
    blocks: [
      { key: "finishes", labelKey: "admin:pageBlocks.blocks.finishes", hideable: true },
      { key: "final_cta", labelKey: "admin:pageBlocks.blocks.finalCta", hideable: true },
    ],
  },
  {
    key: "pricing",
    path: "pricing",
    hideable: true,
    inSitemap: true,
    labelKey: "common:nav.pricing",
    header: { i18nKey: "pricing", testId: "nav-pricing" },
    footer: { column: "product", labelKey: "nav.pricing" },
    blocks: [
      { key: "delivery_modes", labelKey: "admin:pageBlocks.blocks.deliveryModes", hideable: true },
      {
        key: "quantity_discounts",
        labelKey: "admin:pageBlocks.blocks.quantityDiscounts",
        hideable: true,
      },
      { key: "final_cta", labelKey: "admin:pageBlocks.blocks.finalCta", hideable: true },
    ],
  },
  {
    key: "track",
    path: "track",
    hideable: true,
    inSitemap: true,
    labelKey: "common:footer.track",
    footer: { column: "customer", labelKey: "footer.track" },
  },
  {
    key: "contact",
    path: "contact",
    hideable: true,
    inSitemap: true,
    labelKey: "common:nav.contact",
    keepInMinimalWeb: true,
    header: { i18nKey: "contact", testId: "nav-contact" },
    footer: { column: "customer", labelKey: "footer.contact" },
  },
  {
    // ``/services/:slug`` — one page per process; a param route, so the
    // sitemap cannot list it. The switch hides only the header dropdown
    // (KAN-1563): the pages themselves are part of the quote flow — the
    // landing routes an uploaded file for another process there, and the
    // process cards link there — so they are never gated.
    key: "services",
    path: "services",
    hideable: true,
    inSitemap: false,
    labelKey: "admin:publicPages.pages.servicesMenu",
    hintKey: "admin:publicPages.hint.servicesMenu",
    menuOnly: true,
  },
  {
    key: "cart",
    path: "cart",
    hideable: false,
    inSitemap: false,
    labelKey: "common:nav.cart",
    lockReasonKey: "admin:publicPages.lockReason.ordering",
  },
  {
    key: "checkout",
    path: "checkout",
    hideable: false,
    inSitemap: false,
    labelKey: "admin:publicPages.pages.checkout",
    lockReasonKey: "admin:publicPages.lockReason.ordering",
  },
] as const satisfies readonly PublicPage[];

type RegistryEntry = (typeof PUBLIC_PAGES)[number];

export type PublicPageKey = RegistryEntry["key"];

export type ToggleablePageKey = Extract<
  RegistryEntry,
  { hideable: true }
>["key"];

/** Keys a tenant may hide, in editor order. */
export const TOGGLEABLE_PAGES: readonly ToggleablePageKey[] =
  PUBLIC_PAGES.filter(
    (page): page is Extract<RegistryEntry, { hideable: true }> => page.hideable,
  ).map((page) => page.key);

/**
 * The „Minimální web" preset as a visibility map (KAN-1545): every
 * hideable page off except those the registry keeps. What gets saved is
 * this map, not the preset's name — there is no second state to drift.
 */
export function minimalWebVisibility(): Record<ToggleablePageKey, boolean> {
  return Object.fromEntries(
    PUBLIC_PAGES.filter((page) => page.hideable).map((page) => [
      page.key,
      "keepInMinimalWeb" in page,
    ]),
  ) as Record<ToggleablePageKey, boolean>;
}

/** Pages that list blocks, keyed by page. */
export type PageWithBlocks = Extract<RegistryEntry, { blocks: readonly PageBlock[] }>;
export type PageBlockKey<P extends PageWithBlocks["key"] = PageWithBlocks["key"]> =
  Extract<PageWithBlocks, { key: P }>["blocks"][number]["key"];

/** Stored block visibility: ``{page: {block: false}}``; absent = visible. */
export type BlocksVisibility = Record<string, Record<string, boolean>>;

/** The blocks of ``page`` in render order — empty for a page without any. */
export function pageBlocks(key: PublicPageKey): readonly PageBlock[] {
  const page = PUBLIC_PAGES.find((p) => p.key === key)!;
  return "blocks" in page ? page.blocks : [];
}

/** Default = every block visible; a required block is always visible. */
export function isBlockVisible<P extends PageWithBlocks["key"]>(
  blocks: BlocksVisibility | null | undefined,
  page: P,
  block: PageBlockKey<P>,
): boolean {
  const def = pageBlocks(page).find((b) => b.key === block);
  if (!def?.hideable) return true;
  return blocks?.[page]?.[block] !== false;
}

/**
 * The „Jen konfigurátor" preset (KAN-1635): every hideable block of every
 * page off. Paired with every hideable page off, the storefront is its
 * header, the quote surface and its footer — the owner keeps the header
 * and footer (decision 2026-09-25).
 */
export function configuratorOnlyBlocks(): BlocksVisibility {
  return Object.fromEntries(
    PUBLIC_PAGES.filter((page) => "blocks" in page).map((page) => [
      page.key,
      Object.fromEntries(
        pageBlocks(page.key)
          .filter((block) => block.hideable)
          .map((block) => [block.key, false]),
      ),
    ]),
  );
}

/** Every hideable page off — the page half of „Jen konfigurátor". */
export function configuratorOnlyVisibility(): Record<ToggleablePageKey, boolean> {
  return Object.fromEntries(
    TOGGLEABLE_PAGES.map((key) => [key, false]),
  ) as Record<ToggleablePageKey, boolean>;
}

/** Default = every page visible. Missing key in stored map = visible. */
export function isPageVisible(
  visibility: Record<string, boolean> | null | undefined,
  key: PublicPageKey,
): boolean {
  if (!visibility) return true;
  const value = visibility[key];
  return value !== false;
}

export function pageLabelI18nKey(key: PublicPageKey): string {
  return PUBLIC_PAGES.find((page) => page.key === key)!.labelKey;
}

/** ``/cs/materials`` for ``("cs", "materials")``; ``/cs`` for the landing. */
export function publicPageHref(lang: string, key: PublicPageKey): string {
  const path = PUBLIC_PAGES.find((page) => page.key === key)!.path;
  return path ? `/${lang}/${path}` : `/${lang}`;
}
